import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';
import { API_BASE_URL, silentErrors } from '@shared/http';
import { CardWsEvents } from './card.ws-events';
import {
  ApproveThreeDsSchema,
  CardListSchema,
  CardOperationListSchema,
  CardOperationSchema,
  CardSchema,
  CardSensitiveSchema,
  CardStatisticsSchema,
  CardTopupQuoteSchema,
  CardTransactionListSchema,
  CardTypeListSchema,
  FundingFeeQuoteSchema,
  ThreeDsCodeListSchema,
  WalletApprovalSchema,
} from '../model/card.schema';
import type {
  Card,
  CardOperation,
  CardSensitiveData,
  CardStatistics,
  CardTopupQuote,
  CardTransaction,
  CardType,
  FundingFeeQuote,
  Paginated,
  ThreeDsCode,
} from '../model/card.types';
import type { CardEvent } from '../model/card.ws';
import type {
  CardTransport,
  IssueCardRequest,
  ListCardsParams,
  ListOperationsParams,
  ListTransactionsParams,
  QuoteTopupRequest,
  StatisticsParams,
  TopupRequest,
} from './card.transport';

function toParams(source: Record<string, string | number | undefined>): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined && value !== '') params = params.set(key, String(value));
  }
  return params;
}

@Injectable({ providedIn: 'root' })
export class CardHttpTransport implements CardTransport {
  private readonly http = inject(HttpClient);
  private readonly base = inject(API_BASE_URL);
  private readonly wsEvents = inject(CardWsEvents);

  private get cards(): string {
    return `${this.base}/cards`;
  }

  availableTypes(currencyCode?: string, paymentSystem?: string): Observable<CardType[]> {
    return this.http
      .get<unknown>(`${this.cards}/available-types`, {
        params: toParams({ currencyCode, paymentSystem }),
      })
      .pipe(map(raw => CardTypeListSchema.parse(raw)));
  }

  list(params?: ListCardsParams): Observable<Paginated<Card>> {
    return this.http
      .get<unknown>(this.cards, { params: toParams({ ...params }) })
      .pipe(map(raw => CardListSchema.parse(raw)));
  }

  get(cardId: string): Observable<Card> {
    return this.http
      .get<unknown>(`${this.cards}/${cardId}`)
      .pipe(map(raw => CardSchema.parse(raw)));
  }

  issue(request: IssueCardRequest, idempotencyKey: string): Observable<CardOperation> {
    return this.http
      .post<unknown>(this.cards, request, { headers: this.idempotency(idempotencyKey) })
      .pipe(map(raw => CardOperationSchema.parse(raw)));
  }

  rename(cardId: string, cardName: string): Observable<Card> {
    return this.http
      .patch<unknown>(`${this.cards}/${cardId}`, { cardName })
      .pipe(map(raw => CardSchema.parse(raw)));
  }

  setStatus(cardId: string, action: 'FREEZE' | 'UNFREEZE'): Observable<Card> {
    return this.http
      .post<unknown>(`${this.cards}/${cardId}/status`, { action })
      .pipe(map(raw => CardSchema.parse(raw)));
  }

  close(cardId: string): Observable<Card> {
    return this.http
      .delete<unknown>(`${this.cards}/${cardId}`)
      .pipe(map(raw => CardSchema.parse(raw)));
  }

  quoteTopup(cardId: string, request: QuoteTopupRequest): Observable<CardTopupQuote> {
    const body: Record<string, string> = {};
    if (request.sourceAmountRaw !== undefined)
      body['sourceAmountRaw'] = request.sourceAmountRaw.toString();
    if (request.fiatAmountMinor !== undefined)
      body['fiatAmountMinor'] = request.fiatAmountMinor.toString();
    return this.http
      .post<unknown>(`${this.cards}/${cardId}/quote-topup`, body)
      .pipe(map(raw => CardTopupQuoteSchema.parse(raw)));
  }

  fundingFee(walletId: string, amountRaw?: bigint): Observable<FundingFeeQuote> {
    let params = new HttpParams().set('walletId', walletId);
    if (amountRaw !== undefined) params = params.set('amountRaw', amountRaw.toString());

    return this.http
      .get<unknown>(`${this.cards}/funding-fee`, { params, context: silentErrors() })
      .pipe(map(raw => FundingFeeQuoteSchema.parse(raw)));
  }

  topup(cardId: string, request: TopupRequest, idempotencyKey: string): Observable<CardOperation> {
    return this.http
      .post<unknown>(
        `${this.cards}/${cardId}/top-up`,
        { walletId: request.walletId, sourceAmountRaw: request.sourceAmountRaw.toString() },
        { headers: this.idempotency(idempotencyKey) },
      )
      .pipe(map(raw => CardOperationSchema.parse(raw)));
  }

  operations(params?: ListOperationsParams): Observable<Paginated<CardOperation>> {
    return this.http
      .get<unknown>(`${this.cards}/operations`, { params: toParams({ ...params }) })
      .pipe(map(raw => CardOperationListSchema.parse(raw)));
  }

  transactions(params?: ListTransactionsParams): Observable<Paginated<CardTransaction>> {
    return this.http
      .get<unknown>(`${this.cards}/transactions`, { params: toParams({ ...params }) })
      .pipe(map(raw => CardTransactionListSchema.parse(raw)));
  }

  statistics(params?: StatisticsParams): Observable<CardStatistics> {
    return this.http
      .get<unknown>(`${this.cards}/statistics`, { params: toParams({ ...params }) })
      .pipe(map(raw => CardStatisticsSchema.parse(raw)));
  }

  sensitive(cardId: string, freshToken: string): Observable<CardSensitiveData> {
    return this.http
      .get<unknown>(`${this.cards}/${cardId}/sensitive`, {
        headers: new HttpHeaders({ 'x-fresh-token': freshToken }),
      })
      .pipe(map(raw => CardSensitiveSchema.parse(raw)));
  }

  walletApproval(cardId: string): Observable<void> {
    return this.http
      .post<unknown>(`${this.cards}/${cardId}/wallet-approval`, {})
      .pipe(map(raw => void WalletApprovalSchema.parse(raw)));
  }

  threeDsCodes(cardId: string): Observable<ThreeDsCode[]> {
    return this.http
      .get<unknown>(`${this.cards}/${cardId}/3ds-codes`)
      .pipe(map(raw => ThreeDsCodeListSchema.parse(raw)));
  }

  approveThreeDs(cardId: string, codeId: string): Observable<void> {
    return this.http
      .post<unknown>(`${this.cards}/${cardId}/approve-3ds`, { codeId })
      .pipe(map(raw => void ApproveThreeDsSchema.parse(raw)));
  }

  events(): Observable<CardEvent> {
    return this.wsEvents.events();
  }

  private idempotency(key: string): HttpHeaders {
    return key ? new HttpHeaders({ 'Idempotency-Key': key }) : new HttpHeaders();
  }
}
